import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 指定車両一時停止リクエストAPI
 */
export const useLaneUnlockAPI = (): {
  state: APIState;
  postLaneUnlock: () => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postLaneUnlockAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/intersections/mediations/unlock`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postLaneUnlock = useAtomCallback(
    useCallback(
      async (get) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_lane_unlock', { status: 'failed' }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await postLaneUnlockAPI(
          project!.id,
          environment!.environment_id,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_lane_unlock', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_lane_unlock', { status: 'successfully' }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, postLaneUnlockAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postLaneUnlock,
  };
};
